import {
    createStore,
    applyMiddleware,
    combineReducers,
    AnyAction
} from 'redux';
import {
    persistStore,
    persistReducer
} from 'redux-persist';
import {composeWithDevTools} from '@redux-devtools/extension';
import storage from 'redux-persist/lib/storage';
import thunk, {
    ThunkAction,
    ThunkDispatch
} from 'redux-thunk';

// TODO: linter complains for wildcard but it's working... check how to solve this issue
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as reducers from './reducers';

// TODO: linter complains for wildcard but it's working... check how to solve this issue
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as panelReducers from './reducers/panel';

import sizeOf from '~/shared/src/utilities/sizeOf';

import {
    getPanelIndex,
    getPanelProps,
    getPopupIndex
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {BATCH_ACTIONS} from '~/frontend/src/constants';

import panelManagerTransform from '~/frontend/src/transformers/panelManagerTransform';

import {BackendState} from '~/frontend/src/reducers/backendState';
import {ConfigState} from '~/frontend/src/reducers/config';
import {LoginState} from '~/frontend/src/reducers/login';
import {SessionState} from '~/frontend/src/reducers/session';
import {XhrState} from '~/frontend/src/reducers/xhr';
import {ElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {IntercomState} from '~/frontend/src/reducers/intercom';
import {PanelManagerState} from '~/frontend/src/reducers/panelManager';
import {PanelPropsDialogState} from '~/frontend/src/reducers/panelPropsDialog';
import {DebugConfigState} from '~/frontend/src/reducers/debugConfig';
import {AlertMessageState} from '~/frontend/src/reducers/alertMessage';
import {MessageCatalogState} from '~/frontend/src/reducers/messagecatalog';
import {MetaModelState} from '~/frontend/src/reducers/metamodel';


export interface StoreState {
    backendState : BackendState;
    config : ConfigState;
    elusiveData : ElusiveDataState;
    intercom : IntercomState;
    log : { [ p : string ] : any };
    login : LoginState;
    metamodel : MetaModelState;
    messagecatalog : MessageCatalogState;
    panelManager : PanelManagerState;
    panelPropsDialog : PanelPropsDialogState;
    session : SessionState;
    tree : { [ p : string ] : any };
    xhr : XhrState;
    debugConfig : DebugConfigState;
    alertMessage : AlertMessageState;
}

export interface AdditionalGlobalState {
    user : SessionState[ 'user' ]
}

const persistConfig = {
    key       : 'root',
    storage,
    transforms: [ panelManagerTransform ],
    //stateReconciler: autoMergeLevel2,
    blacklist: [
        'backendState',
        'config',
        'debugInfo',
        'elusiveData',
        'intercom',
        'log',
        'login',
        'messagecatalog',
        'metamodel',
        'panelPropsDialog',
        'session',
        'tree',
        'xhr'
    ]
};

const composeEnhancers = composeWithDevTools( {
    name      : 'yoonite RC',
    maxAge    : 20,
    trace     : true,
    traceLimit: 10
} );

/**
 * Add timestamp to all actions
 * @return {function(*): function(*=): *}
 */
const timestampMiddleware = () => ( next : Function ) => ( action : AnyAction ) => {
    action.actionTimestamp = Date.now();
    return next( action );
}

const combinedReducers = combineReducers( { ...reducers } );

const rootReducer = ( state : StoreState | undefined, action : AnyAction ) => {
    const reducedState : any    = combinedReducers( state as any, action as never ),
          additionalGlobalState = {
              user: state?.session?.user
          } as AdditionalGlobalState;

    let retVal = reducedState;

    // panel reducer
    if ( action.panelKey ) {
        // if there is a panelKey set in action -> slice panelProps, subPanelProps from panelManagers state and let panelReducers process this slice
        if ( !action.panelKey.match( /^popup-/ ) && reducedState.panelManager.openPanels ) {
            let panelIndex = getPanelIndex( action.panelKey, reducedState.panelManager.openPanels );

            // panel data reducing
            if ( panelIndex !== undefined ) {
                let panelState = reducedState.panelManager.openPanels[ panelIndex ].panelProps;

                for ( const panelReducer of Object.values( panelReducers ) as Function[] ) {
                    panelState = panelReducer( panelState, action, additionalGlobalState );
                }

                if ( panelState !== reducedState.panelManager.openPanels[ panelIndex ].panelProps ) {
                    retVal = Object.assign( {}, reducedState );
                    retVal.panelManager = Object.assign( {}, retVal.panelManager );
                    retVal.panelManager.openPanels = [].concat( retVal.panelManager.openPanels );
                    retVal.panelManager.openPanels[ panelIndex ] = Object.assign( {}, retVal.panelManager.openPanels[ panelIndex ] );
                    retVal.panelManager.openPanels[ panelIndex ].panelProps = panelState;
                }
            }

            // subPanel(s) data reducing
            else {
                for ( const openPanel of reducedState.panelManager.openPanels ) {
                    const openPanelKey = openPanel.panelProps.panelKey,
                          matches      = action.panelKey.match( `^${openPanelKey}_(.*)$` ),
                          subPanelKey  = matches?.length === 2 ? matches[ 1 ] : undefined;

                    if ( subPanelKey ) {
                        panelIndex = getPanelIndex( openPanelKey, reducedState.panelManager.openPanels );

                        if ( panelIndex !== undefined ) {
                            const subPanelPath = subPanelKey.split( '_' );

                            // getting the current panelState recursively
                            let currentPanelState = reducedState.panelManager.openPanels[ panelIndex ].panelProps;
                            for ( let i = 0; i < subPanelPath.length; i++ ) {
                                currentPanelState = currentPanelState?.subPanelProps?.[ subPanelPath[ i ] ];
                                if ( currentPanelState === undefined ) {
                                    break;
                                }
                            }

                            // reduce the panelState
                            let panelState = currentPanelState;

                            for ( const panelReducer of Object.values( panelReducers ) as Function[] ) {
                                panelState = panelReducer( panelState, action, additionalGlobalState );
                            }

                            // setting the new panelState recursively
                            if ( panelState !== currentPanelState ) {
                                retVal = Object.assign( {}, reducedState );
                                retVal.panelManager = Object.assign( {}, retVal.panelManager );
                                retVal.panelManager.openPanels = [].concat( retVal.panelManager.openPanels );
                                retVal.panelManager.openPanels[ panelIndex ] = Object.assign( {}, retVal.panelManager.openPanels[ panelIndex ] );
                                retVal.panelManager.openPanels[ panelIndex ].panelProps = Object.assign( {}, retVal.panelManager.openPanels[ panelIndex ].panelProps );

                                let parentProps = retVal.panelManager.openPanels[ panelIndex ].panelProps;
                                for ( let i = 0; i < subPanelPath.length; i++ ) {
                                    if ( parentProps.subPanelProps === undefined ) {
                                        parentProps.subPanelProps = {};
                                    }
                                    else {
                                        parentProps.subPanelProps = Object.assign( {}, parentProps.subPanelProps );
                                    }

                                    // end up
                                    if ( i === subPanelPath.length - 1 ) {
                                        parentProps.subPanelProps[ subPanelPath[ i ] ] = panelState;
                                    }

                                    // step deeper
                                    else {
                                        if ( parentProps.subPanelProps[ subPanelPath[ i ] ] === undefined ) {
                                            parentProps.subPanelProps[ subPanelPath[ i ] ] = {};
                                        }
                                        parentProps = parentProps.subPanelProps[ subPanelPath[ i ] ];
                                    }
                                }
                            }
                        }
                        break;
                    }
                }
            }
        }
        else if ( reducedState.panelManager.openPopups ) {
            let popupIndex = getPopupIndex( action.panelKey, reducedState.panelManager.openPopups );

            // panel data reducing
            if ( popupIndex !== undefined ) {
                let panelState = reducedState.panelManager.openPopups[ popupIndex ].panelProps;

                for ( const panelReducer of Object.values( panelReducers ) as Function[] ) {
                    panelState = panelReducer( panelState, action, additionalGlobalState );
                }

                if ( panelState !== reducedState.panelManager.openPopups[ popupIndex ].panelProps ) {
                    retVal = Object.assign( {}, reducedState );
                    retVal.panelManager = Object.assign( {}, retVal.panelManager );
                    retVal.panelManager.openPopups = [].concat( retVal.panelManager.openPopups );
                    retVal.panelManager.openPopups[ popupIndex ] = Object.assign( {}, retVal.panelManager.openPopups[ popupIndex ] );
                    retVal.panelManager.openPopups[ popupIndex ].panelProps = panelState;
                }
            }

            // subPanel(s) data reducing
            else {
                for ( const openPanel of reducedState.panelManager.openPopups ) {
                    const openPanelKey = openPanel.panelProps.panelKey,
                          matches      = action.panelKey.match( `^${openPanelKey}_(.*)$` ),
                          subPanelKey  = matches?.length === 2 ? matches[ 1 ] : undefined;

                    if ( subPanelKey ) {
                        popupIndex = getPopupIndex( openPanelKey, reducedState.panelManager.openPopups );

                        if ( popupIndex !== undefined ) {
                            const subPanelPath = subPanelKey.split( '_' );

                            // getting the current panelState recursively
                            let currentPanelState = reducedState.panelManager.openPopups[ popupIndex ].panelProps;
                            for ( let i = 0; i < subPanelPath.length; i++ ) {
                                currentPanelState = currentPanelState?.subPanelProps?.[ subPanelPath[ i ] ];
                                if ( currentPanelState === undefined ) {
                                    break;
                                }
                            }

                            // reduce the panelState
                            let panelState = currentPanelState;

                            for ( const panelReducer of Object.values( panelReducers ) as Function[] ) {
                                panelState = panelReducer( panelState, action, additionalGlobalState );
                            }

                            // setting the new panelState recursively
                            if ( panelState !== currentPanelState ) {
                                retVal = Object.assign( {}, reducedState );
                                retVal.panelManager = Object.assign( {}, retVal.panelManager );
                                retVal.panelManager.openPopups = [].concat( retVal.panelManager.openPopups );
                                retVal.panelManager.openPopups[ popupIndex ] = Object.assign( {}, retVal.panelManager.openPopups[ popupIndex ] );
                                retVal.panelManager.openPopups[ popupIndex ].panelProps = Object.assign( {}, retVal.panelManager.openPopups[ popupIndex ].panelProps );

                                let parentProps = retVal.panelManager.openPopups[ popupIndex ].panelProps;
                                for ( let i = 0; i < subPanelPath.length; i++ ) {
                                    if ( parentProps.subPanelProps === undefined ) {
                                        parentProps.subPanelProps = {};
                                    }
                                    else {
                                        parentProps.subPanelProps = Object.assign( {}, parentProps.subPanelProps );
                                    }

                                    // end up
                                    if ( i === subPanelPath.length - 1 ) {
                                        parentProps.subPanelProps[ subPanelPath[ i ] ] = panelState;
                                    }

                                    // step deeper
                                    else {
                                        if ( parentProps.subPanelProps[ subPanelPath[ i ] ] === undefined ) {
                                            parentProps.subPanelProps[ subPanelPath[ i ] ] = {};
                                        }
                                        parentProps = parentProps.subPanelProps[ subPanelPath[ i ] ];
                                    }
                                }
                            }
                        }
                        break;
                    }
                }
            }
        }
    }

    // cleanup elusiveData and global.data for non-existing panels (garbage collection)
    // commented out for checking if this causes the frontend to hang up
    /*if ( state?.elusiveData ) {
        const elusiveDataKeys                = Object.keys( state.elusiveData ) as string[],
              usedElusiveDataKeys : string[] = [];

        // collect all used elusiveData keys
        if ( reducedState.panelManager.openPanels ) {
            for ( const openPanel of reducedState.panelManager.openPanels ) {
                const openPanelKey = openPanel.panelProps.panelKey as string;

                for ( const elusiveDataKey of elusiveDataKeys ) {
                    if ( elusiveDataKey === openPanelKey || elusiveDataKey.match( `^${openPanelKey}_.*$` ) ) {
                        usedElusiveDataKeys.push( elusiveDataKey );
                    }
                }
            }
        }
        if ( reducedState.panelManager.openPopups ) {
            for ( const openPanel of reducedState.panelManager.openPopups ) {
                const openPanelKey = openPanel.panelProps.panelKey as string;

                for ( const elusiveDataKey of elusiveDataKeys ) {
                    if ( elusiveDataKey === openPanelKey || elusiveDataKey.match( `^${openPanelKey}_.*$` ) ) {
                        usedElusiveDataKeys.push( elusiveDataKey );
                    }
                }
            }
        }

        // remove all unused elusiveData keys
        if ( usedElusiveDataKeys.length < elusiveDataKeys.length ) {
            retVal = {
                ...retVal,
                elusiveData: { ...retVal.elusiveData }
            };

            for ( const elusiveDataKey of elusiveDataKeys ) {
                if ( !usedElusiveDataKeys.includes( elusiveDataKey ) ) {
                    delete retVal.elusiveData[ elusiveDataKey ];
                    delete global.data[ elusiveDataKey ];
                }
            }
        }
    }*/

    // add debug information
    if ( retVal.debugConfig?.show && retVal.debugConfig.memConsumption?.show ) {
        console.log( retVal );
        retVal = {
            ...retVal,
            debugConfig: {
                ...retVal.debugConfig,
                memConsumption: {
                    show        : true,
                    reduxStore  : sizeOf( retVal ),
                    elusiveData : sizeOf( retVal.elusiveData ),
                    panelManager: sizeOf( retVal.panelManager ),
                    session     : sizeOf( retVal.session ),
                    globalData  : sizeOf( global.data )
                }
            }
        };

        if ( retVal.panelManager.activePanelKey ) {
            const elusiveDataKeys = Object.keys( retVal.elusiveData ?? {} ) as string[],
                  globalDataKeys  = Object.keys( global.data ?? {} ) as string[],
                  panelManager    = sizeOf( getPanelProps( retVal.panelManager.activePanelKey, retVal ) );

            let elusiveData = 0,
                globalData  = 0;

            for ( const elusiveDataKey of elusiveDataKeys ) {
                if ( elusiveDataKey === retVal.panelManager.activePanelKey || elusiveDataKey.match( `^${retVal.panelManager.activePanelKey}[_-](.*)$` ) ) {
                    elusiveData += sizeOf( retVal.elusiveData[ elusiveDataKey ] );
                }
            }

            for ( const globalDataKey of globalDataKeys ) {
                if ( globalDataKey === retVal.panelManager.activePanelKey || globalDataKey.match( `^${retVal.panelManager.activePanelKey}[_-](.*)$` ) ) {
                    globalData += sizeOf( global.data[ globalDataKey ] );
                }
            }

            retVal.debugConfig.memConsumption.currentPanel = {
                reduxTotal: panelManager + elusiveData,
                panelManager,
                elusiveData,
                globalData
            };
        }
    }

    return retVal;
}

const batchingReducer = ( state : StoreState | undefined, action : AnyAction ) => {
    let reducedState : any;

    if ( action.type === BATCH_ACTIONS || action.subType === BATCH_ACTIONS ) {
        reducedState = action.actions.reduce( batchingReducer, state );
    }
    else {
        reducedState = rootReducer( state, action );
    }

    return reducedState;
}

export const store = createStore(
    persistReducer( persistConfig, batchingReducer ),
    composeEnhancers( applyMiddleware( thunk, timestampMiddleware ) )
);

export const persistor = persistStore( store, {
    // avoid starting persistence directly with persistStore call
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    manualPersist: true
} );

export type AppDispatch = ThunkDispatch<StoreState, undefined, AnyAction>;

export type Thunk<R> = ThunkAction<R, StoreState, undefined, AnyAction>;
